import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { AgronodKonton, AuthUser, isLantbrukareUser } from '@/types/auth';

interface AgronodKontoContextValue {
  selectedKontoId: string | null;
  selectedKonto: AgronodKonton | null;
  availableKonton: AgronodKonton[];
  setSelectedKontoId: (id: string) => void;
  isReady: boolean;
}

const AgronodKontoContext = createContext<AgronodKontoContextValue>({
  selectedKontoId: null,
  selectedKonto: null,
  availableKonton: [],
  setSelectedKontoId: () => {},
  isReady: false,
});

interface AgronodKontoProviderProps {
  user: AuthUser | undefined;
  children: React.ReactNode;
}

/**
 * sessionStorage key is tab-scoped by browser semantics — each tab has its own
 * session, so tabs never share selections. The key is NOT scoped to userId so
 * that we can read it synchronously on mount (userId only arrives later via
 * the async `/auth/me` fetch). Cross-user safety inside a single tab is
 * enforced by validating the stored value against the authenticated user's
 * `availableKonton` once the user profile arrives.
 */
const STORAGE_KEY = 'agronodKontoId';

/**
 * Normalises the `agronodKonton` claim into a usable konto list.
 *
 * The claim may arrive as a JSON string straight from the Keycloak token, and
 * the IdP has been seen emitting konton without a `KontoId` (e.g.
 * `{"KontoNamn":"David Dev"}`). Such a konto cannot be selected or sent as
 * `X-Agronodkonto-Id`, so it is dropped here rather than surfacing as an
 * unselectable, key-less menu entry downstream.
 *
 * @param raw - the claim value: an array, a JSON string of one, or undefined
 * @returns konton that carry a non-empty `KontoId`, in claim order
 */
function parseKonton(raw: unknown): AgronodKonton[] {
  let konton: unknown = raw;
  if (typeof raw === 'string') {
    try { konton = JSON.parse(raw); } catch { return []; }
  }
  if (!Array.isArray(konton)) return [];
  return (konton as Partial<AgronodKonton>[]).filter(
    (k): k is AgronodKonton => typeof k?.KontoId === 'string' && k.KontoId.length > 0,
  );
}

/**
 * Provides the currently selected agronodkonto for Lantbrukare users.
 *
 * The selection is:
 *   - restored synchronously from sessionStorage on mount (so requests issued
 *     during the first render already carry the correct X-Agronodkonto-Id),
 *   - validated against the user's konton once the profile arrives (a stored
 *     value that isn't in the new user's list falls back to the first konto),
 *   - persisted on every user-driven change via `setSelectedKontoId`.
 *
 * Tabs stay independent because sessionStorage is per-tab.
 */
export const AgronodKontoProvider: React.FC<AgronodKontoProviderProps> = ({ user, children }) => {
  const availableKonton = useMemo(() => {
    if (!user || !isLantbrukareUser(user)) return [];
    return parseKonton(user.agronodKonton);
  }, [user]);

  // Synchronous read on mount — works on reload because sessionStorage is
  // already populated before React runs. The fetched userId is not needed
  // here; cross-user safety is handled by the validation effect below.
  const [selectedKontoId, setSelectedKontoIdRaw] = useState<string | null>(() => {
    return sessionStorage.getItem(STORAGE_KEY);
  });

  // Track whether the context is ready to use.
  //   - Unknown user (profile still loading)  → not ready, show loading UI.
  //   - B2B user (no konto needed)             → ready as soon as user arrives.
  //   - Lantbrukare user                       → ready once availableKonton is validated.
  // "Unknown" must default to `false` so a fresh-login render never leaks through
  // before we know the user's realm — a `true` default would let children fire
  // konto-scoped queries with a null header.
  const [isReady, setIsReady] = useState<boolean>(
    user !== undefined && !isLantbrukareUser(user),
  );

  const setSelectedKontoId = useCallback((id: string) => {
    setSelectedKontoIdRaw(id);
    sessionStorage.setItem(STORAGE_KEY, id);
  }, []);

  // Validate the stored/current selection against the authenticated user and
  // drive `isReady` to true along the appropriate branch for each user type.
  //
  //   - No user yet          → bail; the next effect run will handle it.
  //   - B2B user             → immediately ready (no konto selection to make).
  //   - Lantbrukare with 0 konton → bail; shouldn't happen in practice.
  //   - Lantbrukare otherwise → validate stored selection, fall back to first,
  //                             then mark ready.
  useEffect(() => {
    if (!user) return;

    if (!isLantbrukareUser(user)) {
      setIsReady(true);
      return;
    }

    if (availableKonton.length === 0) return;

    const stillValid = selectedKontoId && availableKonton.some((k) => k.KontoId === selectedKontoId);
    if (!stillValid) {
      const first = availableKonton[0]!.KontoId;
      setSelectedKontoIdRaw(first);
      sessionStorage.setItem(STORAGE_KEY, first);
    }

    setIsReady(true);
  }, [user, availableKonton, selectedKontoId]);

  const selectedKonto = useMemo(
    () => availableKonton.find((k) => k.KontoId === selectedKontoId) ?? null,
    [availableKonton, selectedKontoId]
  );

  const value = useMemo(
    () => ({ selectedKontoId, selectedKonto, availableKonton, setSelectedKontoId, isReady }),
    [selectedKontoId, selectedKonto, availableKonton, setSelectedKontoId, isReady]
  );

  return <AgronodKontoContext.Provider value={value}>{children}</AgronodKontoContext.Provider>;
};

/** Returns the selected konto context. Safe to call in any component under AgronodKontoProvider. */
export const useAgronodKonto = (): AgronodKontoContextValue => useContext(AgronodKontoContext);
